import { useMemo, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import Map, { Marker, Popup, NavigationControl } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";

import logoImg from "../assets/marca/logo-truck.svg";
import foodtruckImg from "../assets/foodtruck.webp";
import pinSvg from "../assets/pin.svg";

type EventItem = {
  id: string;
  title: string;
  category?: string;
  date: string;
  place: string;
  lat: number;
  lng: number;
  featured?: boolean;
};

const events: EventItem[] = [
  {
    id: "1",
    title: "Stacy Burgers",
    category: "",
    date: "2026-04-12",
    place: "Mendoza, Argentina",
    lat: -32.902238,
    lng: -68.859483,
    featured: true,
  },
];

function formatEventDate(date: string) {
  try {
    return new Intl.DateTimeFormat("es-AR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(new Date(`${date}T00:00:00`));
  } catch {
    return date;
  }
}

function ArrowIcon() {
  return (
    <svg
      className="shrink-0"
      width="20"
      height="20"
      viewBox="0 0 14 10"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M1 5H13M13 5L9 1M13 5L9 9"
        stroke="#F2F1DE"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function CustomPin({ active = false }: { active?: boolean }) {
  return (
    <motion.div
      whileHover={{ scale: 1.08 }}
      className={`h-[42px] w-[42px] cursor-pointer transition-transform duration-200 ${
        active ? "scale-[1.12]" : "scale-100"
      }`}
    >
      <img
        src={pinSvg}
        alt="Pin Stacy"
        className="block h-full w-full"
      />
    </motion.div>
  );
}

export default function MapSection({ className = "" }: { className?: string }) {
  const [selected, setSelected] = useState<EventItem | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  const initialViewState = useMemo(
    () => ({
      longitude: -68.8458,
      latitude: -32.8895,
      zoom: 12,
    }),
    []
  );

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
    layoutEffect: false,
  });

  const sectionOpacity = useTransform(
    scrollYProgress,
    [0, 0.12, 0.9, 1],
    [0, 1, 1, 0.96]
  );
  const sectionScale = useTransform(
    scrollYProgress,
    [0, 0.15, 0.9, 1],
    [0.985, 1, 1, 0.992]
  );

  const leftY = useTransform(scrollYProgress, [0, 1], [16, -16]);
  const mapY = useTransform(scrollYProgress, [0, 1], [20, -20]);
  const bottomImageY = useTransform(scrollYProgress, [0, 1], [28, -28]);
  const bottomTextY = useTransform(scrollYProgress, [0, 1], [12, -12]);

  return (
    <section
      id="foodtruck"
      ref={sectionRef}
      className={`relative w-full bg-[#F2F1DE] px-4 py-16 sm:px-6 sm:py-20 lg:px-10 xl:px-16 ${className}`}
    >
      <motion.div
        className="mx-auto flex w-full max-w-[1920px] flex-col gap-12 lg:gap-14"
        style={{
          opacity: sectionOpacity,
          scale: sectionScale,
        }}
      >
        <div className="grid w-full grid-cols-1 gap-8 xl:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] xl:items-start xl:gap-12">
          <motion.div
            className="flex min-w-0 flex-col gap-6 text-[#181819]"
            style={{ y: leftY }}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false, amount: 0.22 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.img
              src={logoImg}
              alt="Stacy"
              className="h-auto w-[110px] object-contain sm:w-[140px] lg:w-[220px]"
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.22 }}
              transition={{
                duration: 0.45,
                delay: 0.05,
                ease: [0.22, 1, 0.36, 1],
              }}
            />

            <motion.h2
              className="m-0 font-['Excessive_Extra_Condensed'] leading-[0.9] tracking-[0.02em] uppercase text-[#181819]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.22 }}
              transition={{
                duration: 0.55,
                delay: 0.1,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <span className="block text-[clamp(7rem,12vw,10.5rem)]">
                LLEVÁ STACY A TU EVENTO
              </span>
              {/* <span className="block text-[clamp(5rem,12vw,10.5rem)]">
                A TU EVENTO
              </span> */}
            </motion.h2>

            <motion.p
              className="max-w-[700px] font-['Roboto_Mono',monospace] text-[clamp(1.1rem,2.2vw,2rem)] leading-[1.14] tracking-[0.02em] text-[#181819]"
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.22 }}
              transition={{
                duration: 0.5,
                delay: 0.16,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              Nuestro foodtruck está listo para caer en festivales, eventos
              privados y encuentros donde haga falta{" "}
              <span className="font-bold">
                buena comida, ritmo y personalidad.
              </span>
            </motion.p>

            <motion.a
              href="https://whatsform.com/zrgqrv?utm_source=ig&utm_medium=social&utm_content=link_in_bio&fbclid=PAb21jcAQpTlZleHRuA2FlbQIxMQBzcnRjBmFwcF9pZA81NjcwNjczNDMzNTI0MjcAAacn55EjjA0X-9x6vPEVUFF08nA8TSK0AhZrG-ZgHADaU_GvSOHU83-A_rF6qQ_aem_TzR7xp09a0zEXPEHquSWQw"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.22 }}
              transition={{
                duration: 0.5,
                delay: 0.22,
                ease: [0.22, 1, 0.36, 1],
              }}
              whileHover={{
                scale: 1.05,
                boxShadow: "0 0 20px rgba(186, 70, 67, 0.5)",
              }}
              whileTap={{ scale: 0.95 }}
              className="flex min-h-[64px] max-w-[580px] w-full items-center justify-center gap-2.5 rounded-lg bg-[#BA4643] px-6 py-4 sm:w-auto sm:min-w-[280px]"
            >
              <span className="font-['Roboto_Mono',monospace] text-lg font-medium tracking-[0.02em] text-[#F2F1DE] sm:text-xl">
                QUIERO EL FOODTRUCK
              </span>
              <ArrowIcon />
            </motion.a>
          </motion.div>

          <motion.div
            className="flex min-w-0 flex-col gap-4 sm:gap-5"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false, amount: 0.22 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              className="text-[#181819]"
              style={{ y: bottomTextY }}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.22 }}
              transition={{
                duration: 0.5,
                delay: 0.12,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <p className="font-['Roboto_Mono',monospace] text-[clamp(1.25rem,2vw,2rem)] tracking-[0.12em] text-[#181819]">
                <strong>PRÓXIMAS PARADAS</strong>
              </p>
              <p className="max-w-[620px] font-['Roboto_Mono',monospace] text-[clamp(1rem,1.4vw,1.75rem)] leading-[1.15] tracking-[0.02em] text-[#181819]">
                Descubrí dónde vamos a estar.
              </p>
            </motion.div>

            <motion.div
              style={{ y: mapY }}
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden rounded-[24px] border-[3px] border-solid border-[#BA4643] shadow-[0px_12px_42px_-4px_rgba(24,24,25,0.12),0px_8px_18px_-6px_rgba(24,24,25,0.12)]"
            >
              <div className="relative overflow-hidden rounded-[22px]">
                <div className="h-[clamp(300px,38vw,500px)] w-full">
                  <Map
                    initialViewState={initialViewState}
                    mapStyle="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
                    onClick={() => setSelected(null)}
                  >
                    <NavigationControl position="top-right" />

                    {events.map((event) => (
                      <Marker
                        key={event.id}
                        longitude={event.lng}
                        latitude={event.lat}
                        anchor="bottom"
                        onClick={(e) => {
                          e.originalEvent.stopPropagation();
                          setSelected(event);
                        }}
                      >
                        <CustomPin active={selected?.id === event.id} />
                      </Marker>
                    ))}

                    {selected && (
                      <Popup
                        longitude={selected.lng}
                        latitude={selected.lat}
                        anchor="top"
                        closeOnClick={false}
                        onClose={() => setSelected(null)}
                        offset={0}
                        maxWidth="320px"
                        className="stacy-map-popup"
                        focusAfterOpen={false}
                      >
                        <div className="flex min-w-[220px] flex-col gap-2 p-2">
                          <strong className="block font-['Roboto_Mono',monospace] text-[20px] leading-[1] text-[#181819]">
                            {selected.title}
                          </strong>
                          <div className="font-['Roboto_Mono',monospace] text-[13px] leading-[18px] text-[#181819]">
                            {selected.place}
                          </div>
                          <div className="font-['Roboto_Mono',monospace] text-[13px] leading-[18px] text-[#181819]">
                            {formatEventDate(selected.date)}
                          </div>
                          {selected.category ? (
                            <div className="font-['Roboto_Mono',monospace] text-[12px] uppercase tracking-[0.08em] text-[#181819]">
                              {selected.category}
                            </div>
                          ) : null}
                        </div>
                      </Popup>
                    )}
                  </Map>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          style={{ y: bottomImageY }}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.2 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          whileHover={{ scale: 1.01 }}
          className="overflow-hidden rounded-[24px] shadow-[0px_18px_50px_-10px_rgba(24,24,25,0.18)]"
        >
          <img
            src={foodtruckImg}
            alt="Foodtruck Stacy en evento"
            className="h-[clamp(300px,45vw,560px)] w-full object-cover"
          />
        </motion.div>
      </motion.div>
    </section>
  );
}